"use client";

import { useState, useRef, useEffect } from "react";
import { EditorField } from "@/config/ecoBuilder/types";
import { MoveHorizontal } from "lucide-react";
import { usePageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { cn } from "@/lib/utils";
import { PaddingHandle } from "@/components/ecoBuilder/editor/PaddingHandle";
import { RichTextRenderer } from "@/components/ecoBuilder/atoms/RichTextRenderer";

interface BeforeAfterSliderProps {
    id: string;
    imageBefore?: string;
    imageAfter?: string;
    labelBefore?: string;
    labelAfter?: string;
    title?: string;
    description?: string;
    sliderColor?: string;
    textColor?: string;
    paddingTop?: number;
    paddingBottom?: number;
    isSelected?: boolean;
    onUpdateProps?: (newProps: Partial<BeforeAfterSliderProps>) => void;
}



export function BeforeAfterSlider({
    id,
    imageBefore = BeforeAfterSliderDefaultProps.imageBefore,
    imageAfter = BeforeAfterSliderDefaultProps.imageAfter,
    labelBefore = BeforeAfterSliderDefaultProps.labelBefore,
    labelAfter = BeforeAfterSliderDefaultProps.labelAfter,
    title = BeforeAfterSliderDefaultProps.title,
    description = BeforeAfterSliderDefaultProps.description,
    sliderColor = BeforeAfterSliderDefaultProps.sliderColor,
    textColor = BeforeAfterSliderDefaultProps.textColor,
    paddingTop = BeforeAfterSliderDefaultProps.paddingTop,
    paddingBottom = BeforeAfterSliderDefaultProps.paddingBottom,
    isSelected,
    onUpdateProps
}: BeforeAfterSliderProps) {
    const { themeSettings } = usePageContext();
    // If sliderColor is default white, maybe we don't change it to primary? White is good for slider.
    // But textColor?
    const finalTextColor = textColor === "#000000" ? (themeSettings?.textColor || textColor) : textColor;

    const [sliderPosition, setSliderPosition] = useState(50);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = () => setIsResizing(true);
    const handleMouseUp = () => setIsResizing(false);

    const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isResizing || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const clientX = 'touches' in e && e.touches.length > 0 ? e.touches[0]!.clientX : (e as React.MouseEvent).clientX;

        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        const percentage = (x / rect.width) * 100;

        setSliderPosition(percentage);
    };

    useEffect(() => {
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("touchend", handleMouseUp);
        return () => {
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("touchend", handleMouseUp);
        };
    }, []);

    // If no images configured in editor (or placeholders)
    if (!imageBefore && !imageAfter) {
        return (
            <div className="p-8 text-center bg-muted rounded-lg text-muted-foreground border-2 border-dashed border-border">
                يرجى إضافة صور قبل وبعد من الإعدادات
            </div>
        );
    }

    const handlePaddingChange = (side: 'top' | 'bottom') => (newVal: number) => {
        if (!onUpdateProps) return;
        if (side === 'top') onUpdateProps({ paddingTop: newVal });
        else onUpdateProps({ paddingBottom: newVal });
    };

    return (
        <div
            id={id}
            className={cn(
                "w-full max-w-4xl mx-auto px-4 relative group transition-all duration-300"
            )}
            dir="rtl"
            style={{
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
            }}
        >
            {/* Optional Header */}
            {(title || description) && (
                <div className="text-center mb-8 space-y-4">
                    {title && (
                        <div
                            className="text-3xl font-bold md:text-4xl"
                            style={{ color: finalTextColor }}
                        >
                            <RichTextRenderer html={title} />
                        </div>
                    )}
                    {description && (
                        <div
                            className="text-lg opacity-80 max-w-2xl mx-auto"
                            style={{ color: finalTextColor }}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    )}
                </div>
            )}

            {/* Slider Container */}
            <div
                ref={containerRef}
                className="relative w-full aspect-[4/3] md:aspect-[16/9] overflow-hidden rounded-xl shadow-xl select-none cursor-ew-resize group border border-border/50"
                onMouseMove={handleMouseMove}
                onTouchMove={handleMouseMove}
                onMouseDown={handleMouseDown}
                onTouchStart={handleMouseDown}
            >
                {/* Image After (Background) */}
                <img
                    src={imageAfter}
                    alt="After"
                    className="absolute top-0 left-0 w-full h-full object-cover"
                    draggable={false}
                />

                {/* Image Before (Clipped) */}
                <div
                    className="absolute top-0 left-0 h-full overflow-hidden shadow-2xl"
                    style={{ width: `${sliderPosition}%`, borderRight: `4px solid ${sliderColor}` }}
                >
                    <img
                        src={imageBefore}
                        alt="Before"
                        className="absolute top-0 left-0 max-w-none h-full object-cover"
                        style={{ width: containerRef.current?.offsetWidth || '100%' }} // Keep image static relative to container
                        draggable={false}
                    />
                </div>

                {/* Labels (Overlay on top of everything) */}
                <span className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-bold z-40 backdrop-blur-sm pointer-events-none transition-opacity">
                    {labelAfter}
                </span>
                <span className="absolute top-4 left-4 bg-white/90 text-black px-3 py-1 rounded-full text-sm font-bold z-40 shadow-sm backdrop-blur-sm pointer-events-none transition-opacity">
                    {labelBefore}
                </span>

                {/* Slider Handle */}
                <div
                    className="absolute top-0 bottom-0 w-12 -ml-6 flex items-center justify-center z-50"
                    style={{ left: `${sliderPosition}%` }}
                >
                    <div
                        className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: sliderColor, color: finalTextColor }}
                    >
                        <MoveHorizontal className="w-6 h-6" />
                    </div>
                </div>
            </div>
            {/* Padding Handles (Only when selected) */}
            {isSelected && onUpdateProps && (
                <>
                    <PaddingHandle
                        side="top"
                        currentPadding={paddingTop}
                        onPaddingChange={handlePaddingChange('top')}
                    />
                    <PaddingHandle
                        side="bottom"
                        currentPadding={paddingBottom}
                        onPaddingChange={handlePaddingChange('bottom')}
                    />
                </>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// REGISTRY EXPORTS
// ═══════════════════════════════════════════════════════════════

export const BeforeAfterSliderDefaultProps = {
    imageBefore: "https://placehold.co/800x600/e2e8f0/1e293b?text=Before",
    imageAfter: "https://placehold.co/800x600/fbbf24/000000?text=After",
    labelBefore: "قبل",
    labelAfter: "بعد",
    title: "شاهد الفرق بنفسك!",
    description: "بفضل تركيبتنا المتطورة، ستحصل على نتائج مذهلة من أول استخدام.",
    sliderColor: "#ffffff",
    textColor: "#000000",
    paddingTop: 60,
    paddingBottom: 60,
};

export const BeforeAfterSliderSchema: EditorField[] = [
    {
        key: "title",
        label: "العنوان الرئيسي",
        type: "richtext",
        defaultValue: "شاهد الفرق بنفسك!"
    },
    {
        key: "description",
        label: "الوصف (يدعم HTML)",
        type: "richtext",
        defaultValue: "<p>وصف توضيحي...</p>"
    },
    {
        key: "imageBefore",
        label: "صورة قبل",
        type: "image"
    },
    {
        key: "imageAfter",
        label: "صورة بعد",
        type: "image"
    },
    {
        key: "labelBefore",
        label: "نص: قبل",
        type: "text",
        defaultValue: "قبل"
    },
    {
        key: "labelAfter",
        label: "نص: بعد",
        type: "text",
        defaultValue: "بعد"
    },
    {
        key: "sliderColor",
        label: "لون المقبض والحدود",
        type: "color",
        defaultValue: "#ffffff"
    },
    {
        key: "textColor",
        label: "لون النصوص",
        type: "color",
        defaultValue: "#000000"
    },
    {
        key: "paddingTop",
        label: "مسافة علوية",
        type: "slider",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" }
    },
    {
        key: "paddingBottom",
        label: "مسافة سفلية",
        type: "slider",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" }
    }
];
